<template>
  <OurPage data-cy="users-page" :title="$t('title.dealersCompany._')" gray class="users-page">
    <CompanyList
      :items="companies"
      :headers="companyListHeaders"
      :pagination-data="paginationData"
      :is-loading="isLoading"
      @pageChange="getData"
      @onClickRow="onClickRow"
    />

    <FilterModal :show.sync="isShownFilterModal" />

    <template #header-right>
      <div class="buttons">
        <OurButton
          variant="thirdary"
          filled
          :text="$t('button.filters')"
          @click="onClickFilterButton"
        >
          <OurSvgIcon v-if="isMobileDevice" :src="icons.FilterAltFill" />
        </OurButton>

        <TableColumnsSettings
          v-if="!isMobileDevice && isTableColumnsReady"
          :headers="tableHeaders"
          :table-key="tablePreferenceKey"
          data-cy="dealer-company-list-columns"
        />
      </div>
    </template>
  </OurPage>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import { allCompaniesQuery, companiesQuery, allCustomersQuery } from "./gql/queries";
import { allManagersQuery } from "@/gql/managers.queries";
import CompanyList from "./_components/CompanyList.vue";
import FilterModal from "../filter/Modal";
import tableColumnsMixin from "@/mixins/tableColumns.mixin";
import TableColumnsSettings from "@/components/TableColumnsSettings.vue";
import { buildDealerCompanyListTableHeaders } from "@/views/settings/dealer-company/index/config/dealerCompanyListTableHeaders";

export default {
  name: "DealerCompany",

  components: { CompanyList, FilterModal, TableColumnsSettings },

  mixins: [apolloMixin, tableColumnsMixin],

  data() {
    return {
      companies: [],
      paginationData: {},
      isShownFilterModal: false,
      isLoading: false,
      tableColumnsId: "dealer-company-list",
    };
  },

  computed: {
    ...mapState("dealerCompany", ["filtersForm"]),
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    ...mapGetters("user", ["userProfile", "isUserDealer", "isUserDealerAdmin"]),
    ...mapGetters("dealerCompany", ["getStatus", "getFiltersOptions"]),

    addRoute() {
      return { name: "DealerCompanyAdd" };
    },

    isShownAddButton() {
      return (this.isUserDealer && this.isUserDealerAdmin) || !this.isUserDealer;
    },

    icons: () => ({
      FilterAltFill: require("@material-symbols/svg-400/outlined/filter_alt-fill.svg"),
    }),

    tableHeaders() {
      return buildDealerCompanyListTableHeaders({
        t: (key) => this.$t(key),
      });
    },

    companyListHeaders() {
      if (!this.isTableColumnsReady) {
        return null;
      }

      return this.visibleTableHeaders;
    },
  },

  watch: {
    filtersForm: {
      handler: "onChangeFiltersForm",
      deep: true,
    },
  },

  async created() {
    await this.getAccessGroupsAndManagers();
  },

  beforeDestroy() {
    this.SET_FILTERS_FORM({});
  },

  methods: {
    ...mapMutations("dealerCompany", ["SET_FILTERS_FORM", "SET_MANAGERS", "SET_COMPANIES"]),
    ...mapMutations("dealerUsers", ["SET_CUSTOMERS"]),

    async getAccessGroupsAndManagers() {
      const { allManagers } = await this.$get(allManagersQuery);

      if (allManagers) {
        this.SET_MANAGERS(allManagers);
      }

      const { allCompanies } = await this.$get(allCompaniesQuery);

      if (allCompanies) {
        this.SET_COMPANIES(allCompanies);
      }
    },
    async getData(page = parseInt(this.$route.query.page) ?? 1) {
      this.isLoading = true;

      try {
        const { allCustomer } = await this.$get(allCustomersQuery);

        this.SET_CUSTOMERS(allCustomer);

        const filtersOptions = this.getFiltersOptions();

        const { companies } = await this.$get(companiesQuery, { page, filtersOptions });

        if (companies) {
          this.companies = companies.data.map((company) => {
            const { id, name, isActive, taxNumber, manager, customer } = company;

            return {
              id,
              company: name,
              user: manager?.name,
              customer: customer,
              taxNumber: taxNumber,
              bonusProgramLevel: customer?.bonusProgramLevel?.name,
              status: this.getStatus(isActive ? "activated" : "deactivated"),
              page: "DealerCompanyEdit",
            };
          });

          this.paginationData = companies.paginatorInfo;
        }
      } finally {
        this.isLoading = false;
      }
    },

    onClickFilterButton() {
      this.isShownFilterModal = true;
    },

    onChangeFiltersForm() {
      this.getData();
    },

    onClickRow(id) {
      this.$router.push({ name: "DealerCompanyEdit", params: { id } });
    },
  },
};
</script>

<style lang="postcss" scoped>
.users-page {
  .buttons {
    @apply flex items-center justify-between gap-4 sm:justify-start;
  }
}
</style>
